import {useState} from 'react'
import {useNavigate, Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {DataTable, type Column} from '@/components/data-table'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/components/ui/dropdown-menu'
import {ListCard, ListEmptyState} from '@/components/section-card'
import {HelpIcon} from '@/components/help-icon'
import {ChevronGlyph} from '@/components/ui/icons'
import {PageHeading} from '@/components/page-heading'
import {NoAccessState} from '@/components/no-access-state'
import {PlanValidacionTarjeta} from '@/components/bateria/plan-validacion-tarjeta'
import {NuevaBateriaDoor} from '@/components/bateria/nueva-bateria-door'
import {EliminarEvaluacionDialog} from '@/components/bateria/eliminar-evaluacion-dialog'
import {useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {useBateriaSolicitudesAdmin} from '@/hooks/bateria/use-bateria-solicitudes'
import {useDebouncedValue} from '@/hooks/common/use-debounced-value'
import {
  useEntregarEvaluacion,
  useImprimirFormasPdf,
  useValidarLote,
  useWorklist,
  useWorklistResumen,
  WORKLIST_PAGE_SIZE,
  type BateriaEvaluacionFase,
  type BateriaWorklistItem,
} from '@/hooks/bateria/use-bateria'
import {translateApiError} from '@/lib/errors'
import {publicarAviso} from '@/lib/avisos'
import {FASE_TONE} from '@/lib/bateria'
import {Hint} from '@/components/ui/hint'
import {tope} from '@/lib/campos-texto'

// «Dudas» y «Sin ubicar» comparten `fase=revision`: el servidor no distingue cuál la disparó.
// «Detenidos» no tiene filtro propio; `necesita_atencion` sirve porque el servidor ordena los bloqueados primero.
type Vista = 'dudas' | 'sinubicar' | 'listos' | 'detenidos'

function filtroDeVista(vista: Vista | null): {fase?: BateriaEvaluacionFase; necesitaAtencion?: boolean} {
  switch (vista) {
    case 'dudas':
    case 'sinubicar':
      return {fase: 'revision'}
    case 'listos':
      return {fase: 'listo_para_informe'}
    case 'detenidos':
      return {necesitaAtencion: true}
    default:
      return {}
  }
}

function StatCard({
  label,
  value,
  active,
  onClick,
  testId,
}: {
  label: string
  value: number
  active: boolean
  onClick: () => void
  testId: string
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-pressed={active}
      className={`surface surface-raised group relative p-5 text-left ${active ? 'ring-2 ring-ring' : ''}`}>
      <span
        aria-hidden="true"
        className="absolute top-5 right-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary">
        <ChevronGlyph size={14} className="-rotate-90" />
      </span>
      <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">{label}</p>
      <p className="mt-2 font-heading text-[28px] leading-none text-foreground">{value}</p>
    </button>
  )
}

function AvanceCell({validados, total}: {validados: number; total: number}) {
  const pct = total === 0 ? 0 : Math.min(100, Math.round((validados / total) * 100))
  return (
    <div className="min-w-[6rem]">
      <span className="font-mono text-xs tabular-nums text-foreground">
        {validados}/{total}
      </span>
      <span aria-hidden="true" className="mt-1 block h-1.5 w-full overflow-hidden rounded-full bg-track">
        <span className="block h-full rounded-full bg-primary" style={{width: `${pct}%`}} />
      </span>
    </div>
  )
}

function PendientesCell({item}: {item: BateriaWorklistItem}) {
  const {t} = useTranslation()
  if (item.dudas_pendientes === 0 && item.cuarentena_pendiente === 0) {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  return (
    <span className="block text-xs text-muted-foreground">
      {item.dudas_pendientes > 0 && t('admin.bateria.worklist.pendientesDudas', {count: item.dudas_pendientes})}
      {item.dudas_pendientes > 0 && item.cuarentena_pendiente > 0 && ' · '}
      {item.cuarentena_pendiente > 0 &&
        t('admin.bateria.worklist.pendientesSinUbicar', {count: item.cuarentena_pendiente})}
    </span>
  )
}

function worklistColumns(t: (key: string, opts?: Record<string, unknown>) => string): Column<BateriaWorklistItem>[] {
  return [
    {
      key: 'empresa',
      header: t('admin.bateria.worklist.colEmpresa'),
      cell: (item) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{item.business.name}</p>
          <p className="truncate font-mono text-xs text-muted-foreground">{item.business.nit}</p>
        </div>
      ),
    },
    {
      key: 'fase',
      header: t('admin.bateria.worklist.colFase'),
      width: 'w-32',
      headerAside: (
        <HelpIcon
          label={t('common.help.iconLabelFor', {campo: t('admin.bateria.worklist.colFase')})}
          testId="worklist-fase-help">
          {t('admin.bateria.worklist.colFaseHelp')}
        </HelpIcon>
      ),
      cell: (item) => (
        <CeldaEstado chip={<Tag tone={FASE_TONE[item.fase]}>{t(`admin.bateria.fase.${item.fase}`)}</Tag>} />
      ),
    },
    {
      key: 'avance',
      header: t('admin.bateria.worklist.colAvance'),
      width: 'w-28',
      cell: (item) => <AvanceCell validados={item.validados} total={item.n_paquetes} />,
    },
    {
      key: 'pendientes',
      header: t('admin.bateria.worklist.colPendientes'),
      width: 'w-44',
      cell: (item) => <PendientesCell item={item} />,
    },
    {
      key: 'dias',
      header: t('admin.bateria.worklist.colDias'),
      width: 'w-24',
      cell: (item) => <span className="font-mono text-xs text-muted-foreground">{item.dias_sin_avance ?? '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: 'w-56',
      className: 'text-right',
      cell: (item) => (
        <div className="flex items-center justify-end gap-1">
          <WorklistPrimary item={item} />
          <WorklistKebab item={item} />
        </div>
      ),
    },
  ]
}

function WorklistPrimary({item}: {item: BateriaWorklistItem}) {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const validar = useValidarLote(item.business.id, item.evaluacion_id)
  const entregar = useEntregarEvaluacion(item.business.id, item.evaluacion_id)
  const lotePath = `/admin/bateria/empresas/${item.business.id}/evaluaciones/${item.evaluacion_id}`
  const testId = `worklist-primaria-${item.evaluacion_id}`

  if (item.estado === 'cerrada') {
    return (
      <Button
        size="xs"
        variant="outline"
        data-testid={testId}
        disabled={entregar.isPending}
        // La fila no tiene dónde pintar un error: sale por la burbuja global, con su texto propio.
        onClick={() =>
          entregar.mutate(undefined, {
            onError: () =>
              publicarAviso(t('admin.bateria.lifecycle.entregar'), t('admin.bateria.lifecycle.entregarError')),
          })
        }>
        {t('admin.bateria.worklist.primaria.entregar')}
      </Button>
    )
  }

  switch (item.fase) {
    case 'roster':
      return (
        <Button size="xs" variant="outline" data-testid={testId} onClick={() => navigate(lotePath)}>
          {t('admin.bateria.worklist.primaria.abrirLote')}
        </Button>
      )
    case 'campo':
      // A la carga rápida de «Escaneos»: la de la banda de acciones pide confirmar un mapeo antes.
      return (
        <Button
          size="xs"
          variant="outline"
          data-testid={testId}
          onClick={() => navigate(`${lotePath}?tab=gestion#lote-escaneos`)}>
          {t('admin.bateria.worklist.primaria.subirEscaneos')}
        </Button>
      )
    case 'lectura':
      return (
        <Button size="xs" variant="outline" data-testid={testId} onClick={() => navigate(lotePath)}>
          {t('admin.bateria.worklist.primaria.verProgreso')}
        </Button>
      )
    case 'revision':
      // `?ir_a=ubicar`, no `?solo=ubicar`: ese filtra a cero tarjetas en el backend (ver barra-estado.tsx).
      return item.cuarentena_pendiente > 0 ? (
        <Button
          size="xs"
          variant="outline"
          data-testid={testId}
          onClick={() => navigate(`${lotePath}/revision?ir_a=ubicar`)}>
          {t('admin.bateria.worklist.primaria.ubicarHojas', {count: item.cuarentena_pendiente})}
        </Button>
      ) : (
        <Button size="xs" variant="outline" data-testid={testId} onClick={() => navigate(`${lotePath}/revision`)}>
          {t('admin.bateria.worklist.primaria.revisarDudas', {count: item.dudas_pendientes})}
        </Button>
      )
    case 'listo_para_informe':
      return (
        <Button
          size="xs"
          variant="outline"
          data-testid={testId}
          disabled={validar.isPending}
          onClick={() =>
            validar.mutate(undefined, {
              onError: () =>
                publicarAviso(
                  t('admin.bateria.worklist.primaria.validar', {count: item.n_paquetes - item.validados}),
                  t('admin.bateria.siguientePaso.validarError')
                ),
            })
          }>
          {t('admin.bateria.worklist.primaria.validar', {count: item.n_paquetes - item.validados})}
        </Button>
      )
    case 'informe':
      return (
        <Button size="xs" variant="outline" data-testid={testId} onClick={() => navigate(`${lotePath}/informe`)}>
          {t('admin.bateria.worklist.primaria.verInforme')}
        </Button>
      )
  }
}

function WorklistKebab({item}: {item: BateriaWorklistItem}) {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const imprimir = useImprimirFormasPdf(item.business.id, item.evaluacion_id)
  const lotePath = `/admin/bateria/empresas/${item.business.id}/evaluaciones/${item.evaluacion_id}`
  const [eliminando, setEliminando] = useState(false)
  const [imprimirError, setImprimirError] = useState<string | null>(null)
  const [imprimirAviso, setImprimirAviso] = useState<string | null>(null)
  return (
    <>
      {imprimirError && <p className="text-xs text-destructive">{imprimirError}</p>}
      {imprimirAviso && <Hint>{imprimirAviso}</Hint>}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon-xs"
            variant="ghost"
            data-testid={`worklist-acciones-${item.evaluacion_id}`}
            aria-label={t('admin.bateria.detail.masAcciones')}>
            <span aria-hidden="true" className="text-base leading-none">
              ⋯
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem data-testid={`worklist-abrir-${item.evaluacion_id}`} onSelect={() => navigate(lotePath)}>
            {t('admin.bateria.worklist.kebab.abrirLote')}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid={`worklist-bitacora-${item.evaluacion_id}`}
            onSelect={() => navigate(`${lotePath}?tab=gestion&sub=bitacora`)}>
            {t('admin.bateria.worklist.kebab.bitacora')}
          </DropdownMenuItem>
          {/* En toda fase: con el lote cerrado la primaria pasa a «Entregar» y este es el camino al informe. */}
          <DropdownMenuItem
            data-testid={`worklist-informe-${item.evaluacion_id}`}
            onSelect={() => navigate(`${lotePath}/informe`)}>
            {t('admin.bateria.worklist.kebab.verInforme')}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid={`worklist-imprimir-${item.evaluacion_id}`}
            onSelect={() => {
              setImprimirError(null)
              setImprimirAviso(null)
              imprimir.mutate(
                {sinImprimir: true},
                {
                  onError: (err) => setImprimirError(translateApiError(err)),
                  onSuccess: (r) =>
                    setImprimirAviso(r.zipEncolado ? t('admin.bateria.bandaDeVerdad.zipEncolado') : null),
                }
              )
            }}>
            {t('admin.bateria.worklist.kebab.imprimirFormas')}
          </DropdownMenuItem>
          {/* Un registro clínico entregado nunca se elimina (Res. 2764/2022), aunque el worklist ya lo excluya. */}
          {item.estado !== 'entregada' && (
            <DropdownMenuItem
              data-testid={`worklist-eliminar-${item.evaluacion_id}`}
              onSelect={() => setEliminando(true)}>
              {t('admin.bateria.worklist.kebab.eliminar')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <EliminarEvaluacionDialog
        businessId={item.business.id}
        evaluacionId={item.evaluacion_id}
        fechaInicio={item.fecha_inicio}
        psicologoName={item.psicologo_name}
        estado={item.estado}
        personasConPaquete={item.n_paquetes}
        open={eliminando}
        onOpenChange={setEliminando}
        onDeleted={() => {
          queryClient.invalidateQueries({queryKey: ['bateria-worklist']})
          queryClient.invalidateQueries({queryKey: ['admin-stats']})
        }}
      />
    </>
  )
}

function WorklistCard({item}: {item: BateriaWorklistItem}) {
  const {t} = useTranslation()
  return (
    <li className="surface-divider border-b py-3 last:border-0" data-testid={`worklist-card-${item.evaluacion_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{item.business.name}</p>
          <p className="truncate font-mono text-xs text-muted-foreground">{item.business.nit}</p>
        </div>
        <Tag tone={FASE_TONE[item.fase]}>{t(`admin.bateria.fase.${item.fase}`)}</Tag>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <AvanceCell validados={item.validados} total={item.n_paquetes} />
        <PendientesCell item={item} />
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-2">
        <WorklistPrimary item={item} />
        <WorklistKebab item={item} />
      </div>
    </li>
  )
}

export function AdminBateriaPage() {
  const {t} = useTranslation()
  const isSystemUser = useIsSystemUser()
  const [vista, setVista] = useState<Vista | null>(null)
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const search = useDebouncedValue(searchInput, 300)

  function toggleVista(next: Vista) {
    setVista((current) => (current === next ? null : next))
    setPage(1)
  }

  const filtro = filtroDeVista(vista)
  const {data, isLoading, isError, refetch} = useWorklist(
    {...filtro, search, page, pageSize: WORKLIST_PAGE_SIZE},
    isSystemUser
  )
  // Endpoint de resumen propio: sumar una página del worklist se queda corto pasadas cien evaluaciones.
  const {data: resumen} = useWorklistResumen(isSystemUser)
  const {data: solicitudes} = useBateriaSolicitudesAdmin(1, isSystemUser, 'pendiente')

  if (!isSystemUser) return <NoAccessState />

  const items = data?.items ?? []
  // Vacía, el primario va dentro del vacío y no arriba; cargando cuenta como vacía para que no salte de sitio.
  const worklistVacia = isLoading || (!isError && items.length === 0 && !search && vista === null)

  return (
    <div className="space-y-5" data-testid="admin-bateria">
      <PageHeading
        title={t('admin.bateria.worklist.title')}
        accion={
          !worklistVacia && (
            <div className="flex flex-wrap gap-2">
              <Button asChild size="sm" variant="outline" data-testid="admin-bateria-solicitudes-cta">
                <Link to="/admin/bateria-solicitudes">
                  {t('admin.bateria.worklist.solicitudes', {count: solicitudes?.total ?? 0})}
                </Link>
              </Button>
              <NuevaBateriaDoor />
            </div>
          )
        }
      />

      <PlanValidacionTarjeta />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="admin-bateria-stats">
        <StatCard
          label={t('admin.bateria.worklist.statDudas')}
          value={resumen?.dudas ?? 0}
          active={vista === 'dudas'}
          onClick={() => toggleVista('dudas')}
          testId="stat-bateria-dudas"
        />
        <StatCard
          label={t('admin.bateria.worklist.statSinUbicar')}
          value={resumen?.sin_ubicar ?? 0}
          active={vista === 'sinubicar'}
          onClick={() => toggleVista('sinubicar')}
          testId="stat-bateria-sin-ubicar"
        />
        <StatCard
          label={t('admin.bateria.worklist.statListos')}
          value={resumen?.listos ?? 0}
          active={vista === 'listos'}
          onClick={() => toggleVista('listos')}
          testId="stat-bateria-listos"
        />
        <StatCard
          label={t('admin.bateria.worklist.statDetenidos')}
          value={resumen?.detenidos ?? 0}
          active={vista === 'detenidos'}
          onClick={() => toggleVista('detenidos')}
          testId="stat-bateria-detenidos"
        />
      </div>

      {isLoading && !data ? (
        <Skeleton className="h-64 w-full" data-testid="admin-bateria-worklist-skeleton" />
      ) : isError ? (
        <div
          className="surface flex flex-col items-center gap-3 px-6 py-10 text-center"
          data-testid="admin-bateria-worklist-error">
          <p className="text-sm text-foreground">{t('admin.bateria.worklist.errorTitle')}</p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            {t('admin.bateria.worklist.retry')}
          </Button>
        </div>
      ) : items.length === 0 && !search && vista === null ? (
        <div className="surface" data-testid="admin-bateria-worklist-empty">
          <ListEmptyState
            page
            title={t('admin.bateria.worklist.emptyTitle')}
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <NuevaBateriaDoor />
                <Button asChild size="sm" variant="outline" data-testid="admin-bateria-empty-solicitudes">
                  <Link to="/admin/bateria-solicitudes">
                    {t('admin.bateria.worklist.solicitudes', {count: solicitudes?.total ?? 0})}
                  </Link>
                </Button>
              </div>
            }
          />
        </div>
      ) : (
        <ListCard
          toolbar={
            <Input
              maxLength={tope('BUSCA')}
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value)
                setPage(1)
              }}
              placeholder={t('admin.bateria.worklist.searchPlaceholder')}
              className="max-w-xs"
              data-testid="admin-bateria-worklist-search"
            />
          }
          footer={
            !!data?.total && (
              <PaginationBar page={page} pageSize={WORKLIST_PAGE_SIZE} total={data.total} onPageChange={setPage} />
            )
          }>
          {items.length === 0 ? (
            <ListEmptyState title={t('admin.bateria.worklist.sinResultados')} />
          ) : (
            <>
              <ul className="px-3 lg:hidden">
                {items.map((item) => (
                  <WorklistCard key={item.evaluacion_id} item={item} />
                ))}
              </ul>
              <div className="hidden lg:block">
                <DataTable
                  columns={worklistColumns(t)}
                  rows={items}
                  rowKey={(item) => item.evaluacion_id}
                  rowTestId={(item) => `worklist-row-${item.evaluacion_id}`}
                />
              </div>
            </>
          )}
        </ListCard>
      )}
    </div>
  )
}
